import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { Field, FieldType, MemberAddeditComponent, MemberAddeditDialogModel } from '../../brokeragency/member-addedit/member-addedit.component';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { FinanceLedgerService } from '../../../services/finance-ledger/finance-ledger.service';
import { Contract, CreateContractRequest, UpdateContractRequest } from '../../../services/ledger-client/types';
import { SessionService } from '../../../services/session/session.service';

const CONTRACT_TYPES = ['SUPPLY', 'DEMAND'];
const CONTRACT_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ENDED'];
const SETTLEMENT_FREQUENCIES = ['MONTHLY', 'QUARTERLY', 'MANUAL'];
const METADATA_APPLY_MODES = ['Merge with existing metadata:merge', 'Replace metadata entirely:replace'];

interface ContractMetadataTemplatePayload {
  contractIds: string[] | string;
  metadataTemplate: Record<string, unknown>;
  applyMode: 'merge' | 'replace';
}

@Component({
    selector: 'app-finance-contracts',
    templateUrl: './finance-contracts.component.html',
    styleUrls: ['./finance-contracts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FinanceContractsComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  allColumnNames: string[] = [];
  items = new MatTableDataSource<Contract>();

  private readonly destroy$ = new Subject<void>();
  private readonly addFields: Array<Field> = [
    {name: 'payload', label: 'Contract JSON', fieldType: FieldType.F_JSON, required: true},
  ];
  private readonly editFields: Array<Field> = [
    {name: 'id', label: 'Contract ID', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'payload', label: 'Contract JSON', fieldType: FieldType.F_JSON, required: true},
  ];
  private readonly metadataTemplateFields: Array<Field> = [
    {name: 'contractIds', label: 'Contracts', fieldType: FieldType.F_DROPDOWN, values: [], required: true, multiple: true},
    {name: 'applyMode', label: 'Apply Mode', fieldType: FieldType.F_DROPDOWN, values: METADATA_APPLY_MODES, required: true},
    {name: 'metadataTemplate', label: 'Template Metadata JSON', fieldType: FieldType.F_JSON, required: true},
  ];
  private readonly reportConfigFields: Array<Field> = [
    {name: 'contractId', label: 'Contract ID', fieldType: FieldType.F_STRING, readOnly: true},
    {name: 'reportConfig', label: 'Report Config JSON', fieldType: FieldType.F_JSON, readOnly: true},
  ];

  constructor(
      private dialog: MatDialog,
      private snackBar: MatSnackBar,
      private spinnerService: SpinnerService,
      private uiAlertService: UiAlertService,
      private financeLedgerService: FinanceLedgerService,
      private sessionService: SessionService,
  ) {
    this.allColumnNames = this.sessionService.isSuperAdmin()
      ? ['name', 'ownerUserId', 'contractType', 'counterpartyType', 'counterpartyId', 'status', 'actionmenu']
      : ['name', 'contractType', 'counterpartyType', 'counterpartyId', 'status', 'actionmenu'];
  }

  ngOnInit(): void {
    this.reloadItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addItem(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.addFields, 'Finance Contract')
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      const payload = this.getValidCreatePayload(dialogResult?.payload);
      if (!payload) {
        return;
      }

      this.financeLedgerService.createContract(payload).then(() => {
        this.reloadItems();
        this.snackBar.open('Contract created', 'Close', {duration: 3000});
      }).catch((error: unknown) => {
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to create contract'), 'Close', {duration: 3000});
      });
    });
  }

  editItem(item: Contract): void {
    this.spinnerService.show('Loading contract...');
    this.financeLedgerService.getContract(item.id).then(contract => {
      this.spinnerService.hide();
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel({
          id: contract.id,
          payload: this.getPayloadFromContract(contract)
        }, this.editFields, 'Finance Contract')
      });

      dialogRef.afterClosed().subscribe(dialogResult => {
        const payload = this.getValidUpdatePayload(dialogResult?.payload);
        if (!payload) {
          return;
        }

        this.financeLedgerService.safeUpdateContract(contract.id, payload).then(() => {
          this.reloadItems();
          this.snackBar.open('Contract updated', 'Close', {duration: 3000});
        }).catch((error: unknown) => {
          this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to update contract'), 'Close', {duration: 3000});
        });
      });
    }).catch((error: unknown) => {
      this.spinnerService.hide();
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load contract'), 'Close', {duration: 3000});
    });
  }

  deleteItem(item: Contract): void {
    this.uiAlertService.presentAlertConfirm('This will mark the contract as ENDED. Continue?').then(async confirm => {
      if (!confirm) {
        return;
      }

      this.spinnerService.show('Ending contract...');
      this.financeLedgerService.safeUpdateContract(item.id, {status: 'ENDED'}).then(() => {
        this.reloadItems();
        this.spinnerService.hide();
        this.snackBar.open('Contract ended', 'Close', {duration: 3000});
      }).catch((error: unknown) => {
        this.spinnerService.hide();
        this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to end contract'), 'Close', {duration: 3000});
      });
    });
  }

  applyMetadataTemplate(item?: Contract): void {
    const defaultContractIds = item ? [item.id] : [];
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel({
        contractIds: defaultContractIds,
        applyMode: 'merge',
        metadataTemplate: {},
      }, this.getMetadataTemplateFields(), 'Contract Metadata Template')
    });

    dialogRef.afterClosed().subscribe((dialogResult: ContractMetadataTemplatePayload | null) => {
      const payload = this.getValidMetadataTemplatePayload(dialogResult);
      if (!payload) {
        return;
      }

      const modeLabel = payload.applyMode === 'replace' ? 'Replacing metadata' : 'Merging metadata';
      this.spinnerService.show(`${modeLabel} for ${payload.contractIds.length} contract(s)...`);

      Promise.all(payload.contractIds.map(contractId => {
        return this.applyTemplateToContract(contractId, payload.metadataTemplate, payload.applyMode)
          .then(() => ({success: true, error: null as unknown}))
          .catch((error: unknown) => ({success: false, error}));
      }))
        .then((results: Array<{ success: boolean; error: unknown }>) => {
          this.spinnerService.hide();

          const succeeded = results.filter(result => result.success).length;
          const failed = results.length - succeeded;

          if (succeeded > 0) {
            this.reloadItems();
          }

          if (failed === 0) {
            this.snackBar.open(`Metadata template applied to ${succeeded} contract(s)`, 'Close', {duration: 3000});
            return;
          }

          const firstError = results.find(result => !result.success);
          const details = firstError ? this.financeLedgerService.getErrorMessage(firstError.error, 'Update failed') : 'Some updates failed';
          this.snackBar.open(`Updated ${succeeded}, failed ${failed}. ${details}`, 'Close', {duration: 5000});
        })
        .catch((error: unknown) => {
          this.spinnerService.hide();
          this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to apply metadata template'), 'Close', {duration: 3000});
        });
    });
  }

  viewReportConfig(item: Contract): void {
    this.spinnerService.show('Loading report config...');
    this.financeLedgerService.getContractReportConfig(item.id).then((config) => {
      this.spinnerService.hide();
      this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel({
          contractId: item.id,
          reportConfig: this.isJsonObject(config) ? config : {},
        }, this.reportConfigFields, 'Contract Report Config', true, 'View'),
      });
    }).catch((error: unknown) => {
      this.spinnerService.hide();
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load report config'), 'Close', {duration: 3000});
    });
  }

  private getPayloadFromContract(contract: Contract): UpdateContractRequest {
    const payload: UpdateContractRequest = {
      name: contract.name || undefined,
      effectiveStartDate: contract.effectiveStartDate || undefined,
      effectiveEndDate: contract.effectiveEndDate ?? undefined,
      isTPA: contract.isTPA,
      settlementFrequency: contract.settlementFrequency,
      status: contract.status,
      metadata: this.isJsonObject(contract.metadata) ? this.toPlainObject(contract.metadata) : undefined,
    };

    if (this.sessionService.isSuperAdmin() && contract.ownerUserId) {
      payload.ownerUserId = contract.ownerUserId;
    }

    return payload;
  }

  private getValidCreatePayload(payload: unknown): CreateContractRequest | null {
    if (!this.validateObject(payload)) {
      return null;
    }

    const candidate = this.toPlainObject(payload);

    const requiredFields = [
      'contractType',
      'counterpartyType',
      'counterpartyId',
      'name',
      'effectiveStartDate',
    ];

    const missingField = requiredFields.find(field => !(field in candidate));
    if (missingField) {
      this.snackBar.open(`Missing required field: ${missingField}`, 'Close', {duration: 3000});
      return null;
    }

    if (!CONTRACT_TYPES.includes(String(candidate.contractType))) {
      this.snackBar.open(`contractType must be one of: ${CONTRACT_TYPES.join(', ')}`, 'Close', {duration: 3000});
      return null;
    }

    if (typeof candidate.counterpartyType !== 'string' || !candidate.counterpartyType.trim()) {
      this.snackBar.open('counterpartyType must be a non-empty string', 'Close', {duration: 3000});
      return null;
    }

    if (typeof candidate.counterpartyId !== 'string' || !candidate.counterpartyId.trim()) {
      this.snackBar.open('counterpartyId must be a non-empty string', 'Close', {duration: 3000});
      return null;
    }

    if (typeof candidate.name !== 'string' || !candidate.name.trim()) {
      this.snackBar.open('name must be a non-empty string', 'Close', {duration: 3000});
      return null;
    }

    if (typeof candidate.effectiveStartDate !== 'string' || Number.isNaN(Date.parse(candidate.effectiveStartDate))) {
      this.snackBar.open('effectiveStartDate must be a valid date string', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.effectiveEndDate && (typeof candidate.effectiveEndDate !== 'string' || Number.isNaN(Date.parse(candidate.effectiveEndDate)))) {
      this.snackBar.open('effectiveEndDate must be a valid date string when provided', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.isTPA !== undefined && typeof candidate.isTPA !== 'boolean') {
      this.snackBar.open('isTPA must be a boolean when provided', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.status && !CONTRACT_STATUSES.includes(String(candidate.status))) {
      this.snackBar.open(`status must be one of: ${CONTRACT_STATUSES.join(', ')}`, 'Close', {duration: 3000});
      return null;
    }

    if (candidate.settlementFrequency && !SETTLEMENT_FREQUENCIES.includes(String(candidate.settlementFrequency))) {
      this.snackBar.open(`settlementFrequency must be one of: ${SETTLEMENT_FREQUENCIES.join(', ')}`, 'Close', {duration: 3000});
      return null;
    }

    if (candidate.metadata !== undefined && !this.isJsonObject(candidate.metadata)) {
      this.snackBar.open('metadata must be a JSON object', 'Close', {duration: 3000});
      return null;
    }

    const createPayload: CreateContractRequest = {
      contractType: candidate.contractType as 'SUPPLY' | 'DEMAND',
      counterpartyType: String(candidate.counterpartyType).trim(),
      counterpartyId: String(candidate.counterpartyId).trim(),
      name: String(candidate.name).trim(),
      effectiveStartDate: String(candidate.effectiveStartDate),
      effectiveEndDate: candidate.effectiveEndDate ? String(candidate.effectiveEndDate) : undefined,
      isTPA: candidate.isTPA as boolean | undefined,
      settlementFrequency: candidate.settlementFrequency as 'MONTHLY' | 'QUARTERLY' | 'MANUAL' | undefined,
      status: candidate.status as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED' | undefined,
      metadata: candidate.metadata as Record<string, unknown> | undefined,
    };

    if (candidate.ownerUserId !== undefined) {
      if (!this.sessionService.isSuperAdmin()) {
        delete candidate.ownerUserId;
      } else if (typeof candidate.ownerUserId !== 'string' || !candidate.ownerUserId.trim()) {
        this.snackBar.open('ownerUserId must be a non-empty string when provided', 'Close', {duration: 3000});
        return null;
      } else {
        createPayload.ownerUserId = candidate.ownerUserId.trim();
      }
    }

    return createPayload;
  }

  private getValidUpdatePayload(payload: unknown): UpdateContractRequest | null {
    if (!this.validateObject(payload)) {
      return null;
    }

    const candidate = this.toPlainObject(payload);

    if (!Object.keys(candidate).length) {
      this.snackBar.open('Contract JSON cannot be empty', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.name !== undefined && (typeof candidate.name !== 'string' || !candidate.name.trim())) {
      this.snackBar.open('name must be a non-empty string when provided', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.effectiveStartDate !== undefined
        && (typeof candidate.effectiveStartDate !== 'string' || Number.isNaN(Date.parse(candidate.effectiveStartDate)))) {
      this.snackBar.open('effectiveStartDate must be a valid date string when provided', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.effectiveEndDate !== undefined
        && candidate.effectiveEndDate !== null
        && (typeof candidate.effectiveEndDate !== 'string' || Number.isNaN(Date.parse(candidate.effectiveEndDate)))) {
      this.snackBar.open('effectiveEndDate must be a valid date string or null', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.isTPA !== undefined && typeof candidate.isTPA !== 'boolean') {
      this.snackBar.open('isTPA must be a boolean when provided', 'Close', {duration: 3000});
      return null;
    }

    if (candidate.status !== undefined && !CONTRACT_STATUSES.includes(String(candidate.status))) {
      this.snackBar.open(`status must be one of: ${CONTRACT_STATUSES.join(', ')}`, 'Close', {duration: 3000});
      return null;
    }

    if (candidate.settlementFrequency !== undefined && !SETTLEMENT_FREQUENCIES.includes(String(candidate.settlementFrequency))) {
      this.snackBar.open(`settlementFrequency must be one of: ${SETTLEMENT_FREQUENCIES.join(', ')}`, 'Close', {duration: 3000});
      return null;
    }

    if (candidate.metadata !== undefined && !this.isJsonObject(candidate.metadata)) {
      this.snackBar.open('metadata must be a JSON object', 'Close', {duration: 3000});
      return null;
    }

    const updatePayload: UpdateContractRequest = {};
    if (candidate.name !== undefined) {
      updatePayload.name = String(candidate.name).trim();
    }
    if (candidate.effectiveStartDate !== undefined) {
      updatePayload.effectiveStartDate = String(candidate.effectiveStartDate);
    }
    if (candidate.effectiveEndDate !== undefined) {
      updatePayload.effectiveEndDate = candidate.effectiveEndDate === null ? null : String(candidate.effectiveEndDate);
    }
    if (candidate.isTPA !== undefined) {
      updatePayload.isTPA = candidate.isTPA as boolean;
    }
    if (candidate.status !== undefined) {
      updatePayload.status = candidate.status as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED';
    }
    if (candidate.settlementFrequency !== undefined) {
      updatePayload.settlementFrequency = candidate.settlementFrequency as 'MONTHLY' | 'QUARTERLY' | 'MANUAL';
    }
    if (candidate.metadata !== undefined) {
      updatePayload.metadata = candidate.metadata as Record<string, unknown>;
    }
    if (candidate.ownerUserId !== undefined) {
      if (this.sessionService.isSuperAdmin() && (typeof candidate.ownerUserId !== 'string' || !candidate.ownerUserId.trim())) {
        this.snackBar.open('ownerUserId must be a non-empty string when provided', 'Close', {duration: 3000});
        return null;
      }

      if (this.sessionService.isSuperAdmin()) {
        updatePayload.ownerUserId = String(candidate.ownerUserId).trim();
      }
    }

    if (!Object.keys(updatePayload).length) {
      this.snackBar.open('Contract JSON cannot be empty', 'Close', {duration: 3000});
      return null;
    }

    return updatePayload;
  }

  private validateObject(payload: unknown): payload is Record<string, unknown> {
    if (!payload) {
      return false;
    }

    if (typeof payload !== 'object' || Array.isArray(payload)) {
      this.snackBar.open('Contract JSON must be an object', 'Close', {duration: 3000});
      return false;
    }

    return true;
  }

  private isJsonObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private toPlainObject(value: unknown): Record<string, unknown> {
    return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
  }

  private getMetadataTemplateFields(): Array<Field> {
    return this.metadataTemplateFields.map(field => ({...field}));
  }

  private getValidMetadataTemplatePayload(payload: ContractMetadataTemplatePayload | null): {
    contractIds: string[];
    metadataTemplate: Record<string, unknown>;
    applyMode: 'merge' | 'replace';
  } | null {
    if (!payload) {
      return null;
    }

    const contractIds = Array.isArray(payload.contractIds)
      ? payload.contractIds.map(value => String(value || '').trim()).filter(Boolean)
      : [String(payload.contractIds || '').trim()].filter(Boolean);

    if (!contractIds.length) {
      this.snackBar.open('Select at least one contract', 'Close', {duration: 3000});
      return null;
    }

    if (!this.isJsonObject(payload.metadataTemplate)) {
      this.snackBar.open('metadataTemplate must be a JSON object', 'Close', {duration: 3000});
      return null;
    }

    const applyMode = payload.applyMode === 'replace' ? 'replace' : 'merge';

    return {
      contractIds,
      metadataTemplate: this.toPlainObject(payload.metadataTemplate),
      applyMode,
    };
  }

  private applyTemplateToContract(contractId: string, template: Record<string, unknown>, applyMode: 'merge' | 'replace'): Promise<Contract> {
    return this.financeLedgerService.getContract(contractId).then((contract) => {
      const existingMetadata = this.isJsonObject(contract.metadata) ? this.toPlainObject(contract.metadata) : {};
      const nextMetadata = applyMode === 'replace'
        ? this.toPlainObject(template)
        : this.mergeMetadata(existingMetadata, template);
      return this.financeLedgerService.safeUpdateContract(contract.id, {metadata: nextMetadata});
    });
  }

  private mergeMetadata(base: Record<string, unknown>, incoming: Record<string, unknown>): Record<string, unknown> {
    const merged: Record<string, unknown> = this.toPlainObject(base);

    Object.entries(incoming).forEach(([key, value]) => {
      if (this.isJsonObject(value) && this.isJsonObject(merged[key])) {
        merged[key] = this.mergeMetadata(merged[key] as Record<string, unknown>, value);
        return;
      }

      if (Array.isArray(value)) {
        merged[key] = [...value];
        return;
      }

      merged[key] = value as unknown;
    });

    return merged;
  }

  private reloadItems(): void {
    this.financeLedgerService.listContracts().then(contracts => {
      this.items.data = contracts.sort((contract1, contract2) => {
        const value1 = (contract1.name || contract1.id || '').toLowerCase();
        const value2 = (contract2.name || contract2.id || '').toLowerCase();
        return value1 > value2 ? 1 : -1;
      });
      this.metadataTemplateFields[0].values = this.items.data.map((contract) => `${contract.name || contract.id}:${contract.id}`);
      this.items.sort = this.sort;
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load contracts'), 'Close', {duration: 3000});
    });
  }
}
